import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useCart } from '../../contexts/CartContext';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ShoppingCart, Check, Package, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import AxiosDB from '@/config/axios2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDurations, setSelectedDurations] = useState({});
  const [discounts, setDiscounts] = useState({});
  const { addToCart, cartItems } = useCart();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await AxiosDB.get(`/products`);
      setProducts(response.data);

    } catch (error) {
      toast.error('Error al cargar los productos');
    } finally {
      setLoading(false);
    }
  };


  const handleDurationChange = (productId, months, price) => {
    setSelectedDurations(prev => ({
      ...prev,
      [productId]: parseInt(months)
    }));
  };

  const handleAddToCart = async (productId, productName) => {
    try {
      const duration = selectedDurations[productId] || 1;
      await addToCart(productId, 1, duration);
      toast.success(`${productName} (${duration} meses) agregado al carrito`);
    } catch (error) {
      toast.error('Error al agregar al carrito');
    }
  };

  const isInCart = (productId) => {
    return cartItems.some(item => item.product_id === productId);
  };

  const getDiscountLabel = (months) => {
    if (months >= 18) return '20% DESC';
    if (months >= 12) return '15% DESC';
    if (months >= 6) return '10% DESC';
    if (months >= 3) return '5% DESC';
    return '';
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]">Cargando...</div>;
  }

  const mainPlans = products.filter(p => p.tipo.includes('Service'));
  const addons = products.filter(p => p.tipo.includes('Add-on'));

  return (
    <div className="space-y-8" data-testid="products-page">
      <div>
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Catálogo de Productos
        </h2>
        <p className="text-gray-600">Elige el plan perfecto y selecciona tu duración con descuentos especiales</p>
      </div>

      {/* Main Plans */}
      <div>
        <h3 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Planes Principales
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mainPlans.map((product, index) => {
            const inCart = isInCart(product.id);
            const duration = selectedDurations[product.id] || 1;
            const discount = discounts[product.id];
            const discountLabel = getDiscountLabel(duration);

            const gradients = [
              'from-blue-500 to-cyan-600',
              'from-purple-500 to-pink-600',
              'from-orange-500 to-red-600',
              'from-green-500 to-emerald-600',
            ];
            const gradient = gradients[index % gradients.length];

            return (
              <Card
                key={product.id}
                className="shadow-xl hover:shadow-2xl overflow-hidden border-0"
                data-testid={`product-${product.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${gradient}`} />
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-2xl mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {product.nombre}
                      </CardTitle>
                      <CardDescription className="text-base">
                        {product.descripcion}
                      </CardDescription>
                    </div>
                  </div>

                  {/* Duration Selector */}
                  <div className="mt-4 space-y-2">
                    <label className="text-sm font-medium">Duración:</label>
                    <div className="flex gap-2 items-center">
                      <Select
                        value={duration.toString()}
                        onValueChange={(value) => handleDurationChange(product.id, value, product.price)}
                        disabled={inCart}
                      >
                        <SelectTrigger data-testid={`duration-select-${product.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[...Array(24)].map((_, i) => {
                            const months = i + 1;
                            const label = getDiscountLabel(months);
                            return (
                              <SelectItem key={months} value={months.toString()}>
                                {months} {months === 1 ? 'mes' : 'meses'}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {discountLabel && (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {discountLabel}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Price Display */}
                  <div className="mt-4">
                    <div className="flex items-baseline gap-2">

                      <span className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        ${(product.precio)}
                      </span>

                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      ${product.price}/mes × {duration} {duration === 1 ? 'mes' : 'meses'}
                    </p>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <p className="font-semibold text-sm text-gray-700">Características:</p>
                    {product.features.slice(0, 4).map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className={`mt-0.5 p-1 rounded-full bg-gradient-to-r ${gradient}`}>
                          <Check className="h-3 w-3 text-white" />
                        </div>
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full"
                    data-testid={`add-to-cart-${product.id}`}
                    disabled={inCart}
                    onClick={() => handleAddToCart(product.servicio, product.nombre)}
                    style={{
                      background: inCart ? '#e5e7eb' : `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
                      color: inCart ? '#6b7280' : 'white',
                      fontWeight: 600
                    }}
                  >
                    {inCart ? (
                      <>
                        <Check className="mr-2 h-4 w-4" /> En el Carrito
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="mr-2 h-4 w-4" /> Agregar al Carrito
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Add-ons */}
      {addons.length > 0 && (
        <div>
          <h3 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Complementos
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addons.map((product) => {
              const inCart = isInCart(product.id);

              return (
                <Card
                  key={product.id}
                  className="shadow-lg hover:shadow-xl border border-gray-200"
                  data-testid={`product-${product.id}`}
                >
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-5 w-5 text-purple-600" />
                      <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                        Add-on
                      </Badge>
                    </div>
                    <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {product.nombre}
                    </CardTitle>
                    <CardDescription>{product.descripcion}</CardDescription>
                    <div className="mt-3">
                      <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        ${product.precio}
                      </span>
                      <span className="text-gray-500 text-sm ml-2">/mes</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-purple-600 mt-0.5" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      className="w-full"
                      data-testid={`add-to-cart-${product.id}`}
                      variant={inCart ? 'secondary' : 'default'}
                      disabled={inCart}
                      onClick={() => handleAddToCart(product.servicio, product.name)}
                      style={!inCart ? {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        fontWeight: 600
                      } : {}}
                    >
                      {inCart ? (
                        <>
                          <Check className="mr-2 h-4 w-4" /> En el Carrito
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="mr-2 h-4 w-4" /> Agregar
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {products.length === 0 && (
        <Card className="shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Package className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-xl font-semibold text-gray-500">No hay productos disponibles</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductList;
